/**
 * resolveLeafletColor — resolve CSS `var(--token)` color strings to their computed
 * color value before handing them to Leaflet.
 *
 * WHY: Leaflet writes `color`/`fillColor` path options as SVG *presentation
 * attributes* (`fill="…"`, `stroke="…"`). SVG presentation attributes do NOT
 * resolve CSS `var(--token)` — an unresolved `var()` makes the browser fall back
 * to the inherited color, collapsing every polygon to ONE color (e.g. every
 * operation on the ops-planning map rendered the same dark green).
 *
 * The DS palette is token-driven, so we keep tokens in source and resolve them to
 * their computed color at *style time* (when the layer is drawn, DOM + theme are
 * ready) via getComputedStyle. This preserves ZERO hardcoded hex in components
 * while giving Leaflet a real color value. SSR-guarded: with no `document` we fall
 * back to the var()'s own fallback, then the raw string.
 *
 * A layer passes its map container as the scope, so a role that depends on the
 * ground it is painted on (the untouched-operation grey over aerial imagery,
 * see `data-fm-map-ground`) resolves where the layer renders, not at the root.
 */
import type { PathOptions } from 'leaflet';

// Matches `var(--token)` and `var(--token, fallback)`.
const VAR_RE = /^var\(\s*(--[\w-]+)\s*(?:,\s*([^)]+))?\)$/;

/**
 * Resolve a single CSS color string. Non-`var()` values (hex, rgb, named,
 * `transparent`) pass through untouched. `var(--token)` is resolved against the
 * computed styles of `scope` (the document root when omitted); nested var()
 * indirection is followed.
 */
export function resolveCssColor(value: string, scope?: Element): string {
  const match = VAR_RE.exec(value.trim());
  if (!match) return value;
  const [, token, fallback] = match;
  if (typeof document !== 'undefined' && typeof getComputedStyle === 'function') {
    const resolved = getComputedStyle(scope ?? document.documentElement)
      .getPropertyValue(token)
      .trim();
    if (resolved) {
      return resolved.startsWith('var(') ? resolveCssColor(resolved, scope) : resolved;
    }
  }
  return fallback ? fallback.trim() : value;
}

/**
 * Return a copy of `options` with `color`/`fillColor` var() tokens resolved to
 * computed color values. Apply this at every point PathOptions reach a Leaflet
 * path (GeoJSON style, Circle/Polygon pathOptions).
 */
export function resolvePathColors<T extends PathOptions>(options: T, scope?: Element): T {
  const out: T = { ...options };
  if (typeof out.color === 'string') out.color = resolveCssColor(out.color, scope);
  if (typeof out.fillColor === 'string') out.fillColor = resolveCssColor(out.fillColor, scope);
  return out;
}
